import { inject, Injectable } from '@angular/core';
import { GeolocationSearchQueryParams } from '@components/store-locator/models';
import { CurrentLanguageService, REGIONS } from '@shared/services/language';
import { LocalitySearchService } from '@shared/services/locality-search';
import { LocalitySearch } from '@shared/services/locality-search/models';
import { LogsService } from '@shared/services/logs';
import { Observable, of, switchMap, take } from 'rxjs';
import { tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class StoreLocatorService {
  private readonly geocodingService = inject(LocalitySearchService);
  private readonly logs = inject(LogsService);
  private readonly language = inject(CurrentLanguageService).language;

  /**
   * Retrieves the geolocation information for a given search location.
   *
   * @param {string}  searchLocation - The location string for which geolocation data is to be retrieved.
   *                                   It should be at least 3 characters long; otherwise, no request is made.
   * @param {boolean} shouldIncludeAddressDetails - Whether the result labels should contain country and zipcode for dataGouv API
   */
  getLocation(searchLocation: string, shouldIncludeAddressDetails = true): Observable<LocalitySearch[] | null> {
    if (searchLocation && searchLocation.length < 3) {
      this.logs.warn('StoreLocatorService', `Search location is too short, not requesting location`);
      return of(null);
    }
    const regionCode = this.language.region;
    const isPostalCode = new RegExp(REGIONS[regionCode].postalCodePattern).test(searchLocation);
    const query: GeolocationSearchQueryParams = {
      q: searchLocation,
      autocomplete: '1',
      type: 'municipality',
      limit: regionCode === 'FR' && isPostalCode ? '10' : '5',
    };
    this.logs.info('StoreLocatorService', `Requesting location for ${searchLocation || 'all stores'}`, query);
    if (regionCode === 'FR') {
      // Try to get location from DataGouv API first
      return this.geocodingService.getByDataGouv(query, shouldIncludeAddressDetails).pipe(
        take(1),
        switchMap((results) => {
          if (results && results.length > 0) {
            return of(results);
          } else {
            return this.geocodingService.getByGoogle(query, shouldIncludeAddressDetails);
          }
        }),
        tap((results) => {
          if (!results || results.length === 0) {
            this.logs.info('StoreLocatorService', `No locations found for: ${searchLocation}`);
          } else {
            this.logs.info('StoreLocatorService', `Found ${results.length} locations for: ${searchLocation}`, results);
          }
        })
      );
    } else {
      return this.geocodingService.getByGoogle(query, shouldIncludeAddressDetails).pipe(
        tap((results) => {
          if (!results || results.length === 0) {
            this.logs.info('StoreLocatorService', `No locations found for: ${searchLocation}`);
          } else {
            this.logs.info('StoreLocatorService', `Found ${results.length} locations for: ${searchLocation}`, results);
          }
        })
      );
    }
  }
}
